import React from 'react';
import { observer } from 'mobx-react';
import { Button, ButtonProps, Icon } from '@canvas-logic/clp-shared-ui';
import styles from './StyleGuideButtons.module.css';

type Variant = NonNullable<ButtonProps['variant']>;
type ThemeMode = 'light' | 'dark';
type ButtonState = 'idle' | 'hover' | 'click' | 'active' | 'disable' | 'focus';

type CssVarStyle = React.CSSProperties & {
  '--button-bg'?: string;
  '--button-color'?: string;
  '--button-outline'?: string;
};

const ArrowLeft = <Icon type="arrow" style={{ transform: 'rotate(180deg)' }} />;

const STATE_ROWS: { key: ButtonState; label: string }[] = [
  { key: 'idle', label: 'idle' },
  { key: 'hover', label: 'hover' },
  { key: 'click', label: 'click' },
  { key: 'active', label: 'Active' },
  { key: 'disable', label: 'disable' },
  { key: 'focus', label: 'focus' },
];

const BUTTON_COLUMNS: {
  key: string;
  iconStart?: ButtonProps['iconStart'];
  iconEnd?: ButtonProps['iconEnd'];
  children?: React.ReactNode;
}[] = [
  { key: 'text', children: 'Button' },
  { key: 'icon-start', iconStart: ArrowLeft, children: 'Button' },
  { key: 'icon-end', iconEnd: 'arrow', children: 'Button' },
  { key: 'icon-both', iconStart: ArrowLeft, iconEnd: 'arrow', children: 'Button' },
  { key: 'icon-only', iconStart: ArrowLeft },
];

const VARIANT_GROUPS: Variant[] = ['primary', 'secondary', 'ternary', 'ghost'];

const HOVER_CLASSES: Record<Variant, string> = {
  primary: styles.forceHover,
  secondary: styles.forceHoverSecondary,
  ternary: styles.forceHoverTernary,
  ghost: styles.forceHoverGhost,
};

const CLICK_CLASSES: Record<Variant, string> = {
  primary: styles.forceClick,
  secondary: styles.forceClickSecondary,
  ternary: styles.forceClickTernary,
  ghost: styles.forceClickGhost,
};

const ACTIVE_STYLES: Record<ThemeMode, Record<Variant, CssVarStyle>> = {
  light: {
    primary: { '--button-bg': 'var(--color-brand-active)', '--button-color': 'var(--color-light-constant)' },
    secondary: { '--button-bg': '#1d1d1d', '--button-color': 'var(--color-light-constant)' },
    ternary: { '--button-bg': 'var(--color-brand-idle)', '--button-color': 'var(--color-light-constant)' },
    ghost: { '--button-bg': '#1d1d1d', '--button-color': 'var(--color-light-constant)' },
  },
  dark: {
    primary: { '--button-bg': 'var(--color-brand-active)', '--button-color': 'var(--color-light-constant)' },
    secondary: { '--button-bg': '#3c3f45', '--button-color': 'var(--color-light-constant)' },
    ternary: { '--button-bg': 'var(--color-brand-idle)', '--button-color': 'var(--color-light-constant)' },
    ghost: { '--button-bg': '#3c3f45', '--button-color': 'var(--color-light-constant)' },
  },
};

const getStateClassName = (state: ButtonState, variant: Variant) => {
  switch (state) {
    case 'hover':
      return HOVER_CLASSES[variant];
    case 'click':
      return CLICK_CLASSES[variant];
    case 'active':
      return styles.forceState;
    case 'focus':
      return styles.forceFocus;
    default:
      return undefined;
  }
};

const getStateStyle = (mode: ThemeMode, state: ButtonState, variant: Variant): CssVarStyle | undefined => {
  if (state === 'active') {
    return ACTIVE_STYLES[mode][variant];
  }

  if (state === 'focus') {
    return { '--button-outline': '2px solid var(--color-focus-40)' };
  }

  return undefined;
};

interface ButtonPreviewProps {
  mode: ThemeMode;
  state: ButtonState;
  variant: Variant;
  iconStart?: ButtonProps['iconStart'];
  iconEnd?: ButtonProps['iconEnd'];
  children?: React.ReactNode;
}

const ButtonPreview: React.FC<ButtonPreviewProps> = ({ mode, state, variant, iconStart, iconEnd, children }) => {
  const wrapperClassName = getStateClassName(state, variant);
  const wrapperStyle = getStateStyle(mode, state, variant);

  return (
    <div className={styles.previewCell}>
      <div className={wrapperClassName} style={wrapperStyle}>
        <Button variant={variant} iconStart={iconStart} iconEnd={iconEnd} disabled={state === 'disable'}>
          {children}
        </Button>
      </div>
    </div>
  );
};

export interface StyleGuideButtonsProps {
  mode?: ThemeMode;
}

/**
 * Buttons style guide panel — mirrors Figma "Button / Light" and "Button / Dark".
 */
export const StyleGuideButtons: React.FC<StyleGuideButtonsProps> = observer(function StyleGuideButtons({
  mode = 'light',
}) {
  const isDark = mode === 'dark';

  return (
    <div className={styles.viewport}>
      <div className={`${styles.panel} ${isDark ? styles.panelDark : styles.panelLight}`} data-theme-mode={mode}>
        <div className={`${styles.head} ${isDark ? styles.headDark : styles.headLight}`}>
          <p className={styles.eyebrow}>Components</p>
          <h2 className={styles.heading}>Button</h2>
        </div>

        <div className={styles.canvas}>
          {VARIANT_GROUPS.map((variant) => (
            <div key={variant} className={styles.variantGroup}>
              <div className={styles.grid}>
                {STATE_ROWS.map((state) => (
                  <React.Fragment key={`${variant}-${state.key}`}>
                    <div className={styles.stateLabel}>{state.label}</div>
                    {BUTTON_COLUMNS.map((column) => (
                      <ButtonPreview
                        key={`${variant}-${state.key}-${column.key}`}
                        mode={mode}
                        state={state.key}
                        variant={variant}
                        iconStart={column.iconStart}
                        iconEnd={column.iconEnd}
                      >
                        {column.children}
                      </ButtonPreview>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});
